import React, { useState, useEffect } from 'react';
import PrivacyPolicyModal from './PrivacyPolicyModal';

// localStorage stores only non-sensitive consent preferences (no PII)
const STORAGE_KEY = 'gensol_cookie_consent';
const GA_ID = 'G-QX6NE881TS';

const applyConsent = (consent) => {
  // Disable Google Analytics if user opted out
  window[`ga-disable-${GA_ID}`] = !consent.analytics;

  // Update Google Consent Mode v2 if gtag is loaded
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage: consent.advertising ? 'granted' : 'denied',
      ad_user_data: consent.advertising ? 'granted' : 'denied',
      ad_personalization: consent.advertising ? 'granted' : 'denied',
      analytics_storage: consent.analytics ? 'granted' : 'denied',
    });
  }
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [advertising, setAdvertising] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      setVisible(true);
    } else {
      try {
        applyConsent(JSON.parse(saved));
      } catch (e) {
        setVisible(true);
      }
    }
  }, []);

  const save = (consent) => {
    const payload = { ...consent, timestamp: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    applyConsent(payload);
    setVisible(false);
  };

  const handleAcceptAll = () => save({ necessary: true, analytics: true, advertising: true });
  const handleRejectAll = () => save({ necessary: true, analytics: false, advertising: false });
  const handleSavePrefs = () => save({ necessary: true, analytics, advertising });

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="cookie-consent-banner"
        className="fixed bottom-0 left-0 right-0 z-[70] bg-white border-t-4 border-orange-500 shadow-2xl"
      >
        <div className="container mx-auto px-4 py-4 md:py-5">
          {!showSettings ? (
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              {/* Cookie icon + text */}
              <div className="flex items-start gap-3 flex-1">
                <span className="text-3xl flex-shrink-0" aria-hidden="true">🍪</span>
                <div>
                  <h3 className="font-bold text-gray-900 text-base md:text-lg mb-1">
                    Usamos cookies para mejorar tu experiencia
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Utilizamos cookies propias y de terceros (Google Analytics y
                    Google Ads) para analizar el tráfico, personalizar anuncios y
                    mejorar nuestros servicios. Puedes aceptar todas, rechazar las
                    no esenciales o configurar tus preferencias. Más información en
                    nuestro{' '}
                    <button
                      type="button"
                      onClick={() => setShowPrivacy(true)}
                      data-testid="cookie-consent-privacy-link"
                      className="text-orange-600 hover:text-orange-700 font-semibold underline"
                    >
                      Aviso de Privacidad
                    </button>
                    .
                  </p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowSettings(true)}
                  data-testid="cookie-consent-configure"
                  className="px-4 py-2.5 text-sm font-semibold text-gray-700 border-2 border-gray-300 hover:border-gray-400 rounded-lg transition-colors"
                >
                  Configurar
                </button>
                <button
                  type="button"
                  onClick={handleRejectAll}
                  data-testid="cookie-consent-reject"
                  className="px-4 py-2.5 text-sm font-semibold text-gray-700 border-2 border-gray-300 hover:border-gray-400 rounded-lg transition-colors"
                >
                  Rechazar
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  data-testid="cookie-consent-accept-all"
                  className="px-5 py-2.5 text-sm font-bold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors hover:scale-[1.02]"
                >
                  Aceptar todas
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-bold text-gray-900 text-base md:text-lg">
                    Configuración de cookies
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Activa o desactiva cada categoría según tus preferencias.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSettings(false)}
                  aria-label="Volver"
                  className="text-gray-400 hover:text-gray-700 p-1"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 max-h-48 overflow-y-auto pr-1">
                {/* Necessary (always on) */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-gray-900 text-sm">Técnicas</span>
                    <span className="text-xs font-bold text-green-600 uppercase">Activas</span>
                  </div>
                  <p className="text-xs text-gray-600">Necesarias para el funcionamiento del sitio.</p>
                </div>

                {/* Analytics */}
                <label className="bg-white border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-orange-400 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-gray-900 text-sm">Analíticas</span>
                    <input
                      type="checkbox"
                      checked={analytics}
                      onChange={(e) => setAnalytics(e.target.checked)}
                      data-testid="cookie-consent-toggle-analytics"
                      className="w-4 h-4 accent-orange-500 cursor-pointer"
                    />
                  </div>
                  <p className="text-xs text-gray-600">Google Analytics — medir tráfico.</p>
                </label>

                {/* Advertising */}
                <label className="bg-white border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-orange-400 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-gray-900 text-sm">Publicitarias</span>
                    <input
                      type="checkbox"
                      checked={advertising}
                      onChange={(e) => setAdvertising(e.target.checked)}
                      data-testid="cookie-consent-toggle-advertising"
                      className="w-4 h-4 accent-orange-500 cursor-pointer"
                    />
                  </div>
                  <p className="text-xs text-gray-600">Google Ads y remarketing.</p>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleRejectAll}
                  className="px-4 py-2.5 text-sm font-semibold text-gray-700 border-2 border-gray-300 hover:border-gray-400 rounded-lg transition-colors"
                >
                  Rechazar todo
                </button>
                <button
                  type="button"
                  onClick={handleSavePrefs}
                  data-testid="cookie-consent-save-prefs"
                  className="px-5 py-2.5 text-sm font-bold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
                >
                  Guardar preferencias
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <PrivacyPolicyModal open={showPrivacy} onOpenChange={setShowPrivacy} />
    </>
  );
};

export default CookieConsent;
